import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { showInkToast } from '../cms/InkToast';
import {
  AlwaysOnConditions,
  Channel,
  channelKind,
  channelMark,
  channelName,
  isPersonalProfile,
  REACH_CHANNELS,
  PUBLISHING_OFF_WORDS,
  publishingOff,
} from './reach';
import { alwaysOnSentence, LegacyFilter, legacyFilterWords } from './alwaysOnSentence';

/**
 * Reach → Automatic: set up or change a sharing rule (an always-on campaign) — "when an article
 * matching these words is published, post it to these channels at these
 * times". The conditions are the apiserver's `conditions` object
 * (reach/services/alwaysOn.js); the form is a plain view of it.
 */

const CREATE_ALWAYS_ON = gql`
  mutation CreateAlwaysOnCampaign($input: CreateProjectInput!) {
    createProject(input: $input) {
      id
    }
  }
`;
const UPDATE_ALWAYS_ON = gql`
  mutation UpdateAlwaysOnCampaign($id: ID!, $input: UpdateProjectInput!) {
    updateProject(id: $id, input: $input) {
      id
    }
  }
`;

export interface AlwaysOnCampaign {
  id: string;
  name: string;
  campaignStory?: string | null;
  conditions?: AlwaysOnConditions | null;
  channels: Array<{ id: string }>;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MEDIA = [
  ['bookmark', 'Links'],
  ['photo', 'Photos'],
  ['video', 'Videos'],
] as const;
const POST_TYPES = [
  ['auto', 'Link or text'],
  ['photo', 'Photo'],
  ['video', 'Video'],
  ['random', 'Mix of links and photos'],
] as const;
type ScheduleMode = 'asap' | 'times' | 'interval' | 'marks';

const words = (v: string) => [
  ...new Set(
    v
      .split(/[,\n]/)
      .map((w) => w.trim())
      .filter(Boolean)
  ),
];
const hm = (v: string) => (/^\d{1,2}:\d{2}$/.test(v) ? v.padStart(5, '0') : null);

interface Form {
  name: string;
  story: string;
  channelIds: Set<string>;
  include: string;
  exclude: string;
  mediaTypes: Set<string>;
  postType: string;
  maxAgeHours: string;
  delayHours: string;
  mode: ScheduleMode;
  times: string;
  intervalMinutes: string;
  marks: Set<number>;
  days: Set<number>;
  start: string;
  end: string;
  mixedHours: boolean;
  legacyFilter: LegacyFilter | null;
  dropLegacy: boolean;
}

function fromCampaign(c: AlwaysOnCampaign | null): Form {
  const k = c?.conditions || {};
  const schedule = k.schedule || [];
  let mode: ScheduleMode = 'asap';
  let times: string[] = [];
  let intervalMinutes = '120';
  let marks = new Set<number>();
  if (schedule.some((e) => e.time)) {
    mode = 'times';
    times = schedule.map((e) => e.time).filter(Boolean) as string[];
  } else if (schedule.length === 1 && (schedule[0].minuteMark ?? 0) === 0) {
    mode = 'interval';
    intervalMinutes = String(schedule[0].intervalMinutes || 60);
  } else if (schedule.length) {
    mode = 'marks';
    marks = new Set(schedule.map((e) => Number(e.minuteMark) || 0));
  }
  const windows = k.windows || [];
  const first = windows[0];
  const mixedHours = windows.some(
    (w) => w.startTime !== first?.startTime || w.endTime !== first?.endTime
  );
  return {
    name: c?.name || '',
    story: c?.campaignStory || '',
    channelIds: new Set((c?.channels || []).map((ch) => ch.id)),
    include: (k.include || []).join(', '),
    exclude: (k.exclude || []).join(', '),
    mediaTypes: new Set((k.mediaTypes || []).map((m) => m.toLowerCase())),
    postType: k.postType && k.postType !== 'post' ? k.postType : 'auto',
    maxAgeHours: String(k.maxAgeHours ?? 24),
    delayHours: String(k.delayHours ?? 0),
    mode,
    times: times.join(', '),
    intervalMinutes,
    marks,
    days: new Set(windows.map((w) => w.dayOfWeek)),
    start: first?.startTime?.slice(0, 5) || '09:00',
    end: first?.endTime?.slice(0, 5) || '17:00',
    mixedHours,
    legacyFilter: k.legacyFilter || null,
    dropLegacy: false,
  };
}

type ConditionsPatch = Omit<AlwaysOnConditions, 'legacyFilter'> & { filterSql?: null };

function toConditions(f: Form): ConditionsPatch {
  let schedule: AlwaysOnConditions['schedule'] = [];
  if (f.mode === 'times') schedule = words(f.times).map((t) => ({ time: hm(t) || t }));
  else if (f.mode === 'interval')
    schedule = [{ minuteMark: 0, intervalMinutes: Math.max(1, Number(f.intervalMinutes) || 60) }];
  else if (f.mode === 'marks')
    schedule = [...f.marks]
      .sort((a, b) => a - b)
      .map((m) => ({ minuteMark: m, intervalMinutes: 60 }));
  const start = hm(f.start) || '00:00';
  const end = hm(f.end) || '23:59';
  const windows = [...f.days]
    .sort()
    .map((d) => ({ dayOfWeek: d, startTime: start, endTime: end, spansMidnight: end < start }));
  return {
    postType: f.postType,
    maxAgeHours: Math.max(1, Number(f.maxAgeHours) || 24),
    delayHours: Math.max(0, Number(f.delayHours) || 0),
    schedule,
    windows,
    include: words(f.include),
    exclude: words(f.exclude),
    mediaTypes: [...f.mediaTypes],
    ...(f.dropLegacy ? { filterSql: null } : {}),
  };
}

export function AlwaysOnEditor({
  campaign,
  onClose,
  onSaved,
}: {
  /** Null to set up a new one. */
  campaign: AlwaysOnCampaign | null;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const { accountId } = useAccount();
  const { data } = useQuery<{ socialProfiles: Channel[] }>(REACH_CHANNELS, {
    variables: { accountId },
    skip: !accountId,
  });
  const channels = useMemo(() => (data?.socialProfiles || []).filter((c) => c.isActive), [data]);
  const [f, setF] = useState<Form>(() => fromCampaign(campaign));
  useEffect(() => setF(fromCampaign(campaign)), [campaign]);
  const [create] = useMutation<{ createProject: { id: string } }>(CREATE_ALWAYS_ON);
  const [update] = useMutation<{ updateProject: { id: string } }>(UPDATE_ALWAYS_ON);
  const [busy, setBusy] = useState(false);

  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setF((p) => ({ ...p, [key]: value }));
  const toggle = <T,>(key: 'channelIds' | 'mediaTypes' | 'marks' | 'days', value: T) =>
    setF((p) => {
      const next = new Set(p[key] as Set<T>);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...p, [key]: next };
    });

  const timesOk = f.mode !== 'times' || words(f.times).every((t) => hm(t));
  const canSave = f.name.trim().length > 0 && f.channelIds.size > 0 && timesOk && !busy;

  const save = async () => {
    if (!accountId || !canSave) return;
    setBusy(true);
    try {
      const conditions = toConditions(f);
      const common = {
        name: f.name.trim(),
        campaignStory: f.story.trim() || null,
        conditions,
        channelIds: [...f.channelIds],
      };
      let id: string;
      if (campaign) {
        const { data: res } = await update({ variables: { id: campaign.id, input: common } });
        id = res?.updateProject.id || campaign.id;
        showInkToast(`${common.name}: saved.`);
      } else {
        const { data: res } = await create({
          variables: { input: { accountId, kind: 'always_on', status: 'active', ...common } },
        });
        id = res?.createProject.id || '';
        showInkToast(`${common.name} is on.`);
      }
      onSaved(id);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  // The rule in one sentence, as it's built (Prototype: "IN SHORT").
  const sentence = alwaysOnSentence({
    include: words(f.include),
    exclude: words(f.exclude),
    mediaTypes: MEDIA.map(([k]) => k).filter((k) => f.mediaTypes.has(k)),
    channels: channels.filter((c) => f.channelIds.has(c.id)).map((c) => channelName(c.platform)),
    mode: f.mode,
    times: words(f.times),
    intervalMinutes: Math.max(1, Number(f.intervalMinutes) || 60),
    marks: [...f.marks],
    delayHours: Math.max(0, Number(f.delayHours) || 0),
    days: [...f.days],
    start: hm(f.start) || f.start,
    end: hm(f.end) || f.end,
  });

  // What still stops it saving, said under the form (Prototype: "Give it a name.").
  const blocker = !f.name.trim()
    ? 'Give it a name.'
    : !f.channelIds.size
      ? 'Pick at least one channel to post to.'
      : !timesOk
        ? 'Times look like 09:00 or 17:30.'
        : '';

  const field = (label: React.ReactNode, body: React.ReactNode, wide = false) => (
    <div className="aoe-field" data-wide={wide ? 'true' : undefined}>
      <span className="aoe-field-label">{label}</span>
      {body}
    </div>
  );

  // Inline in Reach → Automatic, in place of the rule's detail (Prototype:
  // "Sharing rule edit"), with the rules list still on the left.
  return (
    <section className="aoe-inline" aria-labelledby="aoe-title">
      <div className="aoe-head">
        <h2 className="rcp-title" id="aoe-title">
          {campaign ? 'Edit sharing rule' : 'New sharing rule'}
        </h2>
        <p className="aoe-sub">
          Runs on its own each time a matching article is published. It has no end date.
        </p>
      </div>
      <div className="aoe-short" aria-live="polite">
        <div className="aoe-short-label">IN SHORT</div>
        <div className="aoe-short-text">{sentence}</div>
      </div>

      <div className="aoe-grid">
        {field(
          'Name',
          <input
            className="ink-site-input"
            value={f.name}
            autoFocus
            maxLength={200}
            aria-label="Name"
            placeholder="Name"
            onChange={(e) => set('name', e.target.value)}
          />
        )}
        {field(
          <>
            What it does <span className="ink-muted">(optional)</span>
          </>,
          <input
            className="ink-site-input"
            value={f.story}
            maxLength={300}
            aria-label="What it does"
            placeholder="e.g. Every MLB article"
            onChange={(e) => set('story', e.target.value)}
          />
        )}

        {field(
          'Post to',
          <>
            {channels.length ? (
              <div className="aoe-chans">
                {channels.map((c) => {
                  const on = f.channelIds.has(c.id);
                  const personal = isPersonalProfile(c.platform, c.profileId);
                  const off = publishingOff(c);
                  // One the rule already has can still be taken off it.
                  const locked = off && !on;
                  return (
                    <label
                      className="aoe-chan"
                      data-on={on ? 'true' : undefined}
                      data-off={off ? 'true' : undefined}
                      key={c.id}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={locked}
                        onChange={() => toggle('channelIds', c.id)}
                      />
                      <span className="rc-mark rc-mark-sm">{channelMark(c.platform)}</span>
                      <span className="aoe-chan-text">
                        <span className="aoe-chan-name">
                          {c.profileName || c.profileUsername || channelName(c.platform)}
                        </span>
                        <span className="aoe-chan-sub" data-warn={personal ? 'true' : undefined}>
                          {channelName(c.platform)} · {channelKind(c.platform, c.profileId)} ·{' '}
                          {off ? PUBLISHING_OFF_WORDS : ''}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <span className="ink-muted">No channels connected yet. Connect one in Channels.</span>
            )}
            <span className="ink-muted aoe-help">
              {campaign
                ? 'Whether its posts ask you first is set in Approvals.'
                : 'New rules start on Ask me first. Change it in Approvals.'}
            </span>
          </>,
          true
        )}

        {field(
          <>
            Articles that mention <span className="ink-muted">(any of)</span>
          </>,
          <>
            <input
              className="ink-site-input"
              value={f.include}
              aria-label="Articles that mention"
              aria-describedby="aoe-include-help"
              placeholder="Words, separated by commas"
              onChange={(e) => set('include', e.target.value)}
            />
            <span id="aoe-include-help" className="ink-muted aoe-help">
              Leave blank for every article
            </span>
          </>
        )}
        {field(
          'But not',
          <input
            className="ink-site-input"
            value={f.exclude}
            aria-label="But not"
            placeholder="Words, separated by commas"
            onChange={(e) => set('exclude', e.target.value)}
          />
        )}

        {field(
          'Kinds of article',
          <div className="aoe-pills">
            {MEDIA.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className="aoe-pill"
                data-active={f.mediaTypes.has(key) ? 'true' : undefined}
                onClick={() => toggle('mediaTypes', key)}
              >
                {label}
              </button>
            ))}
            <span className="ink-muted">None picked = every kind</span>
          </div>
        )}
        {field(
          'Post as',
          <span className="ink-seg" style={{ alignSelf: 'flex-start', flexWrap: 'wrap' }}>
            {POST_TYPES.map(([key, label]) => (
              <button
                key={key}
                type="button"
                data-active={f.postType === key}
                onClick={() => set('postType', key)}
              >
                {label}
              </button>
            ))}
          </span>
        )}

        {field(
          'Only articles newer than',
          <span className="aoe-unit">
            <input
              className="ink-site-input"
              type="number"
              min={1}
              aria-label="Only articles newer than, in hours"
              value={f.maxAgeHours}
              onChange={(e) => set('maxAgeHours', e.target.value)}
            />
            hours
          </span>
        )}
        {field(
          'Wait after publishing',
          <span className="aoe-unit">
            <input
              className="ink-site-input"
              type="number"
              min={0}
              aria-label="Wait after publishing, in hours"
              value={f.delayHours}
              onChange={(e) => set('delayHours', e.target.value)}
            />
            hours
          </span>
        )}

        {field(
          'When to post',
          <>
            <span className="ink-seg" style={{ alignSelf: 'flex-start', flexWrap: 'wrap' }}>
              {(
                [
                  ['asap', 'As soon as allowed'],
                  ['times', 'At set times'],
                  ['interval', 'Every so often'],
                  ['marks', 'Past each hour'],
                ] as Array<[ScheduleMode, string]>
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  data-active={f.mode === key}
                  onClick={() => set('mode', key)}
                >
                  {label}
                </button>
              ))}
            </span>
            {f.mode === 'times' ? (
              <label
                className="aoe-field-label"
                style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
              >
                Times · 24-hour, separated by commas
                <input
                  className="ink-site-input aoe-narrow"
                  value={f.times}
                  placeholder="HH:MM, HH:MM"
                  onChange={(e) => set('times', e.target.value)}
                  aria-invalid={!timesOk}
                />
              </label>
            ) : null}
            {f.mode === 'interval' ? (
              <span className="aoe-unit">
                every
                <input
                  className="ink-site-input"
                  type="number"
                  min={5}
                  step={5}
                  aria-label="Every how many minutes"
                  value={f.intervalMinutes}
                  onChange={(e) => set('intervalMinutes', e.target.value)}
                />
                minutes
              </span>
            ) : null}
            {f.mode === 'marks' ? (
              <div className="aoe-pills">
                {[0, 15, 30, 45].map((m) => (
                  <button
                    key={m}
                    type="button"
                    className="aoe-pill"
                    data-active={f.marks.has(m) ? 'true' : undefined}
                    onClick={() => toggle('marks', m)}
                  >
                    :{String(m).padStart(2, '0')}
                  </button>
                ))}
              </div>
            ) : null}
          </>,
          true
        )}

        {field(
          <>
            Only on <span className="ink-muted">· none picked = any day, any time</span>
          </>,
          <>
            <div className="aoe-pills">
              {DAYS.map((d, i) => (
                <button
                  key={d}
                  type="button"
                  className="aoe-pill"
                  data-active={f.days.has(i) ? 'true' : undefined}
                  onClick={() => toggle('days', i)}
                >
                  {d}
                </button>
              ))}
            </div>
            {f.days.size ? (
              <span className="aoe-unit">
                between
                <input
                  className="ink-site-input"
                  type="time"
                  aria-label="From"
                  value={f.start}
                  onChange={(e) => set('start', e.target.value)}
                />
                and
                <input
                  className="ink-site-input"
                  type="time"
                  aria-label="Until"
                  value={f.end}
                  onChange={(e) => set('end', e.target.value)}
                />
                {f.mixedHours ? (
                  <span className="ink-muted">
                    Hours differed by day; saving sets these hours for every day picked.
                  </span>
                ) : null}
              </span>
            ) : null}
          </>,
          true
        )}

        {f.legacyFilter ? (
          <div className="aoe-legacy" data-wide="true">
            <span>
              <strong>Older filter</strong> — {legacyFilterWords(f.legacyFilter, words(f.include))}
            </span>
            <button
              type="button"
              className="ink-btn ink-btn-quiet"
              data-active={f.dropLegacy ? 'true' : undefined}
              onClick={() => set('dropLegacy', !f.dropLegacy)}
            >
              {f.dropLegacy ? 'Will be removed on save' : 'Remove it'}
            </button>
          </div>
        ) : null}
      </div>

      <div className="aoe-foot">
        <span className="ink-muted">{blocker}</span>
        <span style={{ flex: 1 }} />
        <button type="button" className="ink-btn" disabled={busy} onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="ink-btn ink-btn-primary"
          disabled={!canSave}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : campaign ? 'Save' : 'Turn it on'}
        </button>
      </div>
    </section>
  );
}
